import { apiFetch, apiFetchJson, registerRefreshHandler } from '../lib/apiFetch.js';
import { clearTokens, getRefreshToken, setTokens } from './tokenStore.js';

/**
 * Cross-origin auth client against `api.<env>.qelm.app` (P12 surface):
 *   POST /api/v1/auth/login   — email + password -> { accessToken, refreshToken, user, tenant }
 *   POST /api/v1/auth/refresh — { refreshToken } -> rotated { accessToken, refreshToken }
 *   POST /api/v1/auth/logout  — revoke the refresh family
 *   GET  /api/v1/me           — current { user, tenant }
 *
 * The access token lives in memory (tokenStore); cookies carry the credentialed
 * session via `credentials:'include'` baked into apiFetch. Refresh is wired into
 * apiFetch as the single-flight 401 handler at module load.
 */

export interface SessionUser {
  id: number;
  name: string;
  email: string;
  role: string;
  permissions: string[];
}

export interface SessionTenant {
  id: number;
  name: string;
  subdomain: string;
  logoPath: string | null;
}

export interface SessionImpersonation {
  platformAdminEmail: string;
  startedAt: string;
  supportReason: string | null;
}

/**
 * Optional integrations this tenant may SEE, resolved server-side by `GET /api/v1/me`.
 *
 * Not permissions and not a plan: `servicetitan` is a per-tenant allowlist (the integration belongs
 * to one customer), so it is false for every other tenant including brand-new signups. Absent from
 * the response — an older worker, a read that failed — reads as false, because showing a surface the
 * api worker will 404 is worse than hiding one it would have served.
 *
 * HIDING IS UX, NOT AUTHORIZATION. `serviceTitanGate` in the api worker is the boundary.
 */
export interface SessionFeatures {
  servicetitan: boolean;
  /**
   * Platform shell rollout (top bar + 9-dot launcher + cmd-K). A per-tenant allowlist row in
   * `tenant_features`, same mechanism as `servicetitan`; absent means the tenant still sees the
   * sidebar shell unchanged. The flag only decides WHICH chrome renders — every access rule is
   * identical in both shells.
   */
  new_shell: boolean;
}

/**
 * The company settings `GET /api/v1/me` carries. Only what a screen reads is typed. Absent after a
 * bare login response or an older worker; callers fall back to the browser's own zone.
 */
export interface SessionCompanySettings {
  /** IANA zone the company keeps time in, e.g. `America/Chicago`. */
  timeZone?: string;
  /** Managers may make a phone sign-in code (owners always may). Absent on an older worker. */
  managersIssueLoginCodes?: boolean;
}

export interface Session {
  user: SessionUser;
  tenant: SessionTenant;
  features: SessionFeatures;
  companySettings?: SessionCompanySettings | null;
  impersonation: SessionImpersonation | null;
  analytics?: {
    userAnalyticsId?: string | null;
    tenantAnalyticsId?: string | null;
    impersonatedUserAnalyticsId?: string | null;
    impersonatedTenantAnalyticsId?: string | null;
  } | null;
}

interface LoginResponse extends Omit<Session, 'features'> {
  ok: true;
  features?: Partial<SessionFeatures>;
  accessToken: string;
  accessTokenExpiresIn: number;
  refreshToken: string;
  refreshTokenExpiresAt: string;
}

interface RefreshResponse {
  ok: true;
  rotation: 'rotated' | 'grace';
  accessToken: string;
  accessTokenExpiresIn: number;
  refreshToken: string;
}

/** Every integration flag defaults to OFF, so an absent or partial `features` object hides. */
function readFeatures(features: Partial<SessionFeatures> | null | undefined): SessionFeatures {
  return {
    servicetitan: features?.servicetitan === true,
    new_shell: features?.new_shell === true,
  };
}

/**
 * `turnstileToken` is only needed once the server has asked for a challenge — it answers with
 * `turnstile_required` and the public site key. Sending one unasked is harmless.
 */
export async function login(
  email: string,
  password: string,
  turnstileToken?: string,
): Promise<Session> {
  const data = await apiFetchJson<LoginResponse>('/api/v1/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, ...(turnstileToken ? { turnstileToken } : {}) }),
    skipRefresh: true,
  });
  setTokens({
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
    accessTokenExpiresIn: data.accessTokenExpiresIn,
  });
  return {
    user: data.user,
    tenant: data.tenant,
    features: readFeatures(data.features),
    impersonation: data.impersonation ?? null,
    analytics: data.analytics ?? null,
  };
}

/**
 * Single-flight refresh. Returns true on success (tokens rotated in place),
 * false on failure (tokens cleared — caller routes to sign-in).
 */
export async function refreshSession(): Promise<boolean> {
  // In-session we hold the refresh token in memory and send it in the body. After a page reload
  // that in-memory token is gone — but the HttpOnly refresh cookie (sent via credentials:'include')
  // carries it, so we still attempt the refresh with an empty body and let the cookie do the work.
  const refreshToken = getRefreshToken();

  try {
    const data = await apiFetchJson<RefreshResponse>('/api/v1/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(refreshToken ? { refreshToken } : {}),
      skipRefresh: true,
    });
    setTokens({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      accessTokenExpiresIn: data.accessTokenExpiresIn,
    });
    return true;
  } catch {
    clearTokens();
    return false;
  }
}

export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken();
  try {
    await apiFetch('/api/v1/auth/logout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
      skipRefresh: true,
    });
  } finally {
    clearTokens();
  }
}

export async function exitImpersonation(): Promise<{ platformAdminUrl: string }> {
  try {
    return await apiFetchJson<{ ok: true; platformAdminUrl: string }>(
      '/api/v1/impersonation/exit',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
        skipRefresh: true,
      },
    );
  } finally {
    clearTokens();
  }
}

export async function fetchMe(): Promise<Session> {
  return apiFetchJson<
    { ok: true } & Omit<Session, 'features'> & { features?: Partial<SessionFeatures> }
  >('/api/v1/me', { cache: 'no-store' }).then((data) => ({
    user: data.user,
    tenant: data.tenant,
    features: readFeatures(data.features),
    companySettings: data.companySettings ?? null,
    impersonation: data.impersonation ?? null,
    analytics: data.analytics ?? null,
  }));
}

// Wire refresh into apiFetch's single-flight 401 handler.
registerRefreshHandler(refreshSession);
